import { useState, useReducer, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient"; // Your Supabase client

// Initial state for the reducer
const initialState = {
  data: null, // Fetched data
  isLoading: false, // Whether the operation is in progress
  error: null, // Error state
  success: null, // Success state for insert/update/delete
};

// Reducer function
const supabaseReducer = (state, action) => {
  switch (action.type) {
    case "IS_PENDING":
      return { ...state, isLoading: true, error: null, success: false };
    case "FETCHED_DATA":
      return {
        ...state,
        isLoading: false,
        data: action.payload,
        success: true,
        error: null,
      };
    case "UPDATE_DATA":
      // Ensure that the payload is always treated as an array
      const payload = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];

      const updatedData = state.data.map((item) => {
        // Find the corresponding updated item in the payload
        const updatedItem = payload.find(
          (payloadItem) => payloadItem.id === item.id
        );
        return updatedItem
          ? { ...item, ...updatedItem.updatedFields } // Update the item if it exists in the payload
          : item; // Keep the item unchanged if it isn't in the payload
      });

      return {
        ...state,
        isLoading: false,
        data: updatedData,
        success: true,
        error: null,
      };

    case "DELETE_DATA":
      const filteredData = state.data.filter(
        (item) => item.id !== action.payload.id
      );
      return {
        ...state,
        isLoading: false,
        data: filteredData,
        success: true,
        error: null,
      };
    case "INSERT_DATA":
      return {
        ...state,
        isLoading: false,
        data: [...state.data, action.payload],
        success: true,
        error: null,
      };
    case "ERROR":
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        success: false,
      };
    case "SUCCESS":
      return { ...state, isLoading: false, success: true, error: null };
    default:
      return state;
  }
};

const useSupabase = () => {
  const [state, dispatch] = useReducer(supabaseReducer, initialState);
  const [isCancelled, setIsCancelled] = useState(false); // To prevent state updates after component unmounts

  const dispatchIfNotCancelled = (action) => {
    if (!isCancelled) {
      dispatch(action);
    } else {
    }
  };

  // Fetch data from any table with conditions
  const fetchData = useCallback(async (table, conditions = {}) => {
    dispatch({ type: "IS_PENDING" });

    try {
      let query = supabase.from(table).select();

      // Apply conditions if any
      for (const [key, value] of Object.entries(conditions)) {
        query = query.eq(key, value); // Apply eq condition
      }

      const { data, error } = await query;

      if (error) {
        dispatchIfNotCancelled({ type: "ERROR", payload: error.message });
        return { data: null, error };
      }

      dispatchIfNotCancelled({ type: "FETCHED_DATA", payload: data });
      return data;
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
    }
  }, []);

  // Insert data into any table
  const insertData = async (table, newData) => {
    dispatch({ type: "IS_PENDING" });

    try {
      const { data, error } = await supabase
        .from(table)
        .insert(newData)
        .select();

      if (error) {
        // Don't dispatch to internal state when returning error to component
        return { data: null, error };
      }

      // Dispatch the insert action to update the state
      dispatchIfNotCancelled({ type: "INSERT_DATA", payload: data[0] }); // Assuming `data[0]` is the newly inserted row

      dispatchIfNotCancelled({ type: "SUCCESS" });

      return { data, error: null }; // Return inserted data and no error
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      return { data: null, error: err };
    }
  };

  const upsertData = async (table, updatedData, conditions) => {
    dispatch({ type: "IS_PENDING" });

    try {
      const { data, error } = await supabase
        .from(table)
        .upsert(updatedData, { onConflict: Object.keys(conditions) })
        .select();

      const normalizedData = Array.isArray(updatedData)
        ? updatedData
        : [updatedData];

      if (error) {
        // Don't dispatch to internal state when returning error to component
        return { data: null, error };
      }

      // Dispatch the update to the state
      dispatchIfNotCancelled({
        type: "UPDATE_DATA", // Action type for updating
        payload: normalizedData.map((item) => ({
          id: item.id, // The item's id
          updatedFields: item, // The updated fields
        })),
      });
      // Mark the operation as a success
      dispatchIfNotCancelled({ type: "SUCCESS" });

      return { data, error: null }; // Return updated data and no error
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      return { data: null, error: err };
    }
  };

  // Delete data from any table based on conditions
  const deleteData = async (table, conditions) => {
    dispatch({ type: "IS_PENDING" });

    try {
      const { error } = await supabase.from(table).delete().match(conditions);

      if (error) {
        // Don't dispatch to internal state when returning error to component
        return { data: null, error };
      }

      // Dispatch the delete to the state
      dispatchIfNotCancelled({ type: "DELETE_DATA", payload: conditions });

      dispatchIfNotCancelled({ type: "SUCCESS" });

      return { data: null, error: null }; // Return no data and no error for successful delete
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      return { data: null, error: err };
    }
  };

  useEffect(() => {
    setIsCancelled(false);

    return () => {
      setIsCancelled(true);
    };
  }, []);

  return {
    data: state.data,
    isLoading: state.isLoading,
    error: state.error,
    success: state.success,
    fetchData,
    insertData,
    upsertData,
    deleteData,
  };
};

export default useSupabase;
